import Grid from "@/ui/molecules/Grid";
import type {
    AddExtractionRow,
    ExtractionType,
    FileExtractionInfo,
    FileExtractionInfoWithParent,
    FileInfoWithParent,
} from "../dealTypes";
import { isCoaLockingExtractionType } from "../dealTypes";
import type { UserType } from "../userTypes";
import { canDeleteExtraction, canDeleteFile } from "../userTypes";
import React, { useCallback, useMemo } from "react";
import type {
    ColDef,
    GetRowIdFunc,
    GridOptions,
    IsFullWidthRowParams,
    GetContextMenuItemsParams,
    MenuItemDef,
    CustomCellRendererProps,
    ITooltipParams,
} from "@/ui/molecules/Grid";
import { StatusCellRenderer } from "../DealTable/StatusCellRenderer";
import { CheckboxRenderer } from "../DealTable/CheckboxRenderer";
import Box from "@/ui/atoms/Box";
import clsx from "clsx";
import { Button, Icon, Tooltip } from "@proda-ai/ui-kit";
import { FileNameCellRenderer } from "./FileNameCellRenderer";
import { RowTypeCellRenderer } from "./RowTypeCellRenderer";
import { capitalize } from "../utils";
import { CreateExtractionRenderer } from "./createExtractionRenderer";
import { MUIGlobalStyles as GlobalStyles } from "@proda-ai/ui-kit";
import { CheckboxHeaderRenderer } from "../DealTable/CheckboxHeaderRenderer";
import type { TDealsOverviewTab } from "@/ui/router/routerPaths";
import EmptyGridMessageRenderer, {
    type EmptyGridMessageRendererProps,
} from "@/ui/molecules/EmptyGridMessageRenderer";
import MessageWithButton from "@/ui/molecules/MessageWithButton";
import WarningMessage from "@/ui/atoms/WarningMessage";
import Typography from "@/ui/atoms/Typography";
import GridLoader from "@/ui/molecules/GridLoader";

const INTERNAL_ERROR_MESSAGE =
    "There was an error fetching files, Please try refreshing the page.";
const GRID_EMPTY_MESSAGE = "No files yet";
const GRID_EMPTY_SUBMESSAGE = "Upload your first file to get started";

export const testIds = {
    exportButton: "DealProcessedFiles-exportButton",
};

export const isFileInfoWithParentTypeGuard = (
    file: FileExtractionInfo,
): file is FileInfoWithParent => {
    return "isParent" in file && !("extractionId" in file);
};

export const isExtractionInfoWithParentTypeGuard = (
    file: FileExtractionInfo,
): file is FileExtractionInfoWithParent => {
    return "extractionId" in file;
};

export const isAddExtractionRowTypeGuard = (
    fileExtractionInfo: FileExtractionInfo,
): fileExtractionInfo is AddExtractionRow => {
    return "addExtraction" in fileExtractionInfo;
};

export const isNotUndefinedTypeGuard = <T,>(
    value: T | undefined,
): value is T => {
    return value !== undefined;
};

export const formatDocType = (fileType: string) => {
    if (fileType === "pdf") {
        return "PDF";
    }
    return capitalize(fileType);
};

type DealProcessedFilesProps = {
    fileExtractionInfo: FileExtractionInfo[];
    selectedExtractions: FileExtractionInfoWithParent[];
    selectedDeletableExtractions: FileExtractionInfoWithParent[];
    selectedFiles: FileInfoWithParent[];
    selectedDeletableFiles: FileInfoWithParent[];
    projectFilesIsPending: boolean;
    projectFilesIsError: boolean;
    userType: UserType | undefined;
    onDeleteButtonClicked: () => void;
    onAddExtraction: (params: {
        projectId: number;
        databaseId: number;
        fileId: number;
        activeTab: TDealsOverviewTab;
        fileName: string;
    }) => void;
    onExportExtractions: ({
        extractions,
    }: {
        extractions: {
            extractionId: number;
            extractionType: ExtractionType;
        }[];
    }) => void;
    onDownloadOriginalFiles: (fileIds: number[]) => void;
    getExtractionUrl: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => string;
    getRentRollExtractionUrl: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => string;
    onViewExtraction: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => void;
    onViewRentRollExtraction: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => void;
    toggleExtraction: (extraction: FileExtractionInfoWithParent) => void;
    clearSelection: () => void;
    setSelectedFilesAndExtractions: (
        files: FileInfoWithParent[],
        extractions: FileExtractionInfoWithParent[],
    ) => void;
    getSelectedStatus: (
        fileExtractionInfo: FileExtractionInfoWithParent | FileInfoWithParent,
    ) => "selected" | "intermediate" | "unselected";
    setFileAndChildren: (
        files: FileInfoWithParent[],
        setTo: boolean,
        exclusive: boolean,
        fileExtractionInfo: FileExtractionInfo[],
    ) => void;
    ComponentFormattedDate?: React.ComponentType<{ date: string }>;
};

const DealProcessedFiles: React.FC<DealProcessedFilesProps> = ({
    fileExtractionInfo,
    selectedExtractions,
    selectedDeletableExtractions,
    selectedFiles,
    selectedDeletableFiles,
    projectFilesIsPending,
    projectFilesIsError,
    userType,
    getExtractionUrl,
    getRentRollExtractionUrl,
    onAddExtraction,
    onDeleteButtonClicked,
    onExportExtractions,
    onDownloadOriginalFiles,
    onViewExtraction,
    onViewRentRollExtraction,
    toggleExtraction,
    clearSelection,
    setSelectedFilesAndExtractions,
    getSelectedStatus,
    setFileAndChildren,
    ComponentFormattedDate = (props: { date: string }) => {
        return props.date;
    },
}) => {
    const rowData = fileExtractionInfo;
    const totalItems = fileExtractionInfo.filter(
        fileOrExtraction =>
            isExtractionInfoWithParentTypeGuard(fileOrExtraction) ||
            isFileInfoWithParentTypeGuard(fileOrExtraction),
    ).length;
    const setEntireFiles = React.useCallback(
        (files: FileInfoWithParent[], setTo: boolean, exclusive: boolean) => {
            setFileAndChildren(files, setTo, exclusive, fileExtractionInfo);
        },
        [fileExtractionInfo, setFileAndChildren],
    );

    const isLoading = projectFilesIsPending;

    const selectAll = useCallback(() => {
        setSelectedFilesAndExtractions(
            fileExtractionInfo.filter(fileOrExtraction =>
                isFileInfoWithParentTypeGuard(fileOrExtraction),
            ),
            fileExtractionInfo.filter(fileOrExtraction =>
                isExtractionInfoWithParentTypeGuard(fileOrExtraction),
            ),
        );
    }, [fileExtractionInfo, setSelectedFilesAndExtractions]);

    const getContextMenuItems = useCallback(
        (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
            const data = params.node?.data as FileExtractionInfo;
            const isFile = params.node && isFileInfoWithParentTypeGuard(data);
            const isExtraction =
                params.node && isExtractionInfoWithParentTypeGuard(data);

            if (isExtraction === true && canDeleteExtraction(userType, data)) {
                return [
                    {
                        name: "Delete extraction",
                        action: () => {
                            toggleExtraction(data);
                            onDeleteButtonClicked();
                        },
                        cssClasses: ["delete-item"],
                    },
                ];
            }
            if (isFile === true) {
                const addDocumentToDeal = {
                    name: "Add extraction",
                    action: () =>
                        onAddExtraction({
                            projectId: data.projectId,
                            databaseId: data.databaseId,
                            fileId: data.fileId,
                            activeTab: "documents",
                            fileName: data.originalFilename,
                        }),
                };
                const downloadOriginalFile = {
                    name: "Download original file",
                    action: () => onDownloadOriginalFiles([data.fileId]),
                };
                const deleteFile = {
                    name: "Delete file",
                    action: () => {
                        setEntireFiles([data], true, true);
                        onDeleteButtonClicked();
                    },
                    cssClasses: ["delete-item"],
                };
                const adminOnlyActions = ["separator", deleteFile];
                const allowDeletion = canDeleteFile(userType, data);

                const conditionalAddDocument =
                    data.status !== "with_proda" ? [addDocumentToDeal] : [];

                return allowDeletion
                    ? [
                          ...conditionalAddDocument,
                          downloadOriginalFile,
                          ...adminOnlyActions,
                      ]
                    : [...conditionalAddDocument, downloadOriginalFile];
            }
            return [];
        },
        [
            onAddExtraction,
            onDownloadOriginalFiles,
            setEntireFiles,
            onDeleteButtonClicked,
            toggleExtraction,
            userType,
        ],
    );

    const dealProcessedFilesStyles = {
        ".delete-item": {
            color: "var(--proda-red) !important",
        },
    };

    const ExportableExtractions = useMemo(() => {
        return selectedExtractions.filter(extraction =>
            isCoaLockingExtractionType(extraction.extractionType),
        );
    }, [selectedExtractions]);

    const nonExportableExtractions = useMemo(() => {
        return selectedExtractions.filter(
            extraction => extraction.extractionType === "rent_roll_extraction",
        );
    }, [selectedExtractions]);

    const handleButtonClick = (callback: () => void) => {
        callback();
        clearSelection();
    };

    const handleDeleteButtonClicked = () => {
        setEntireFiles(selectedFiles, true, false);
        onDeleteButtonClicked();
    };

    const DateCellRenderer = React.useMemo(
        // eslint-disable-next-line react/no-unstable-nested-components, react/display-name
        () => (props: CustomCellRendererProps<FileExtractionInfo, string>) => {
            return (
                props.value !== null &&
                props.value !== undefined && (
                    <ComponentFormattedDate date={props.value} />
                )
            );
        },
        [ComponentFormattedDate],
    );

    const columnDefs = useMemo<ColDef<FileExtractionInfo>[]>(
        () => [
            {
                maxWidth: 50,
                minWidth: 50,
                resizable: false,
                field: "fileId",
                suppressHeaderMenuButton: true,
                suppressMovable: true,
                cellRenderer: CheckboxRenderer,
                cellRendererParams: {
                    getSelectedStatus,
                    toggleExtraction,
                    setEntireFiles,
                } as React.ComponentProps<typeof CheckboxRenderer>,
                headerComponent: CheckboxHeaderRenderer,
                headerComponentParams: {
                    clearSelection,
                    selectAll,
                    checked:
                        selectedFiles.length + selectedExtractions.length === 0
                            ? "none"
                            : selectedFiles.length +
                                    selectedExtractions.length >=
                                totalItems
                              ? "all"
                              : "some",
                },
                pinned: "left",
                lockPosition: "left",
            },
            {
                field: "type",
                cellRenderer: RowTypeCellRenderer,
                minWidth: 175,
                width: 175,
            },
            {
                headerName: "Original File",
                field: "originalFilename",
                rowGroup: true,
            },
            {
                headerName: "Extraction ID",
                field: "extractionId",
                valueFormatter: (params: { value: number }) => {
                    return params.value !== null && params.value !== undefined
                        ? Math.abs(params.value).toString()
                        : "";
                },
                tooltipValueGetter: (params: ITooltipParams) => {
                    if (params.value === null || params.value === undefined)
                        return;
                    if (params.value < 0) {
                        return `Rent Roll Extraction ID: ${Math.abs(params.value as number)}`;
                    }
                    return `Operating Statement Extraction ID: ${params.value}`;
                },
            },
            {
                headerName: "File Type",
                field: "fileType",
                valueFormatter: (params: { value: string }) =>
                    formatDocType(params.value),
            },
            {
                headerName: "Date Added",
                field: "uploadedAt",
                cellRenderer: DateCellRenderer,
            },
            {
                headerName: "Added By",
                field: "uploadedBy",
            },
            {
                headerName: "Last Modified",
                field: "modifiedAt",
                cellRenderer: DateCellRenderer,
            },
            {
                headerName: "Last Modified By",
                field: "modifiedBy",
            },
            {
                headerName: "Date Completed",
                field: "completedAt",
                cellRenderer: DateCellRenderer,
            },
            {
                headerName: "Status",
                field: "status",
                cellRenderer: StatusCellRenderer,
            },
        ],
        [
            DateCellRenderer,
            clearSelection,
            getSelectedStatus,
            selectAll,
            selectedExtractions.length,
            selectedFiles.length,
            setEntireFiles,
            toggleExtraction,
            totalItems,
        ],
    );

    const defaultColDef = useMemo<ColDef>(
        () => ({
            flex: 1,
            minWidth: 120,
            sortable: true,
            filter: true,
            resizable: true,
        }),
        [],
    );

    const gridOptions = useMemo(
        () =>
            ({
                treeData: true,
                groupDefaultExpanded: -1,
                getDataPath: file => {
                    if (isFileInfoWithParentTypeGuard(file)) {
                        return [`${file.originalFilename}`];
                    }
                    return [
                        `${file.parentFileName}`,
                        isExtractionInfoWithParentTypeGuard(file)
                            ? `${file.sheetName} Table ${file.extractionId}`
                            : "",
                    ];
                },
                autoSizeStrategy: {
                    type: "fitGridWidth",
                },
                columnMenu: "legacy",
            }) as GridOptions<FileExtractionInfo>,
        [],
    );
    const isFullWidthRow = useCallback((params: IsFullWidthRowParams) => {
        const data = params.rowNode.data as FileExtractionInfo;
        return isAddExtractionRowTypeGuard(data);
    }, []);
    const addExtractionFullRowCellRenderer = useCallback(
        CreateExtractionRenderer,
        [],
    );

    const getRowId: GetRowIdFunc<FileExtractionInfo> = React.useCallback(
        params => {
            if ("addExtraction" in params.data) {
                return `add-extraction-${params.data.fileId}`;
            } else if ("extractionId" in params.data) {
                return String([params.data.fileId, params.data.extractionId]);
            } else {
                return String(params.data.fileId);
            }
        },
        [],
    );

    const renderSelectionTitle = () => {
        if (selectedExtractions.length > 0 && selectedFiles.length > 0) {
            return `${selectedExtractions.length} ${
                selectedExtractions.length > 1 ? "extractions" : "extraction"
            } and ${selectedFiles.length} ${
                selectedFiles.length > 1 ? "files" : "file"
            } selected`;
        } else if (selectedExtractions.length > 0) {
            return `${selectedExtractions.length} ${
                selectedExtractions.length > 1 ? "extractions" : "extraction"
            } selected`;
        } else if (selectedFiles.length > 0) {
            return `${selectedFiles.length} file${
                selectedFiles.length > 1 ? "s" : ""
            } selected`;
        }
        return "";
    };

    const downloadOriginalsText = () => {
        const distinctFileIds = new Set([
            ...selectedExtractions.map(extraction => extraction.fileId),
            ...selectedFiles.map(file => file.fileId),
        ]);
        const distinctFilesCount = distinctFileIds.size;
        if (distinctFilesCount === 1) {
            return "Download Original File";
        }
        return "Download Original Files";
    };

    return (
        <>
            {isLoading ? (
                <GridLoader
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                />
            ) : (
                <>
                    <GlobalStyles styles={dealProcessedFilesStyles} />
                    <Box
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        <Grid<FileExtractionInfo>
                            className={clsx(["deal-processed-files"])}
                            gridTheme={"ag-theme-custom-proda"}
                            getRowId={getRowId}
                            noRowsOverlayComponent={
                                projectFilesIsError
                                    ? MessageWithButton
                                    : EmptyGridMessageRenderer
                            }
                            noRowsOverlayComponentParams={
                                {
                                    gridError: projectFilesIsError,
                                    errorMessage: INTERNAL_ERROR_MESSAGE,
                                    emptyMessage: GRID_EMPTY_MESSAGE,
                                    emptySubMessage: GRID_EMPTY_SUBMESSAGE,
                                } as EmptyGridMessageRendererProps
                            }
                            enableBrowserTooltips={true}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            suppressHeaderFocus={true}
                            getContextMenuItems={getContextMenuItems}
                            isFullWidthRow={isFullWidthRow}
                            gridOptions={gridOptions}
                            fullWidthCellRenderer={
                                addExtractionFullRowCellRenderer
                            }
                            fullWidthCellRendererParams={{
                                handleAddExtractionButtonClicked: ({
                                    projectId,
                                    databaseId,
                                    fileId,
                                    fileName,
                                }: {
                                    projectId: number;
                                    databaseId: number;
                                    fileId: number;
                                    fileName: string;
                                }) => {
                                    onAddExtraction({
                                        projectId,
                                        databaseId,
                                        fileId,
                                        activeTab: "documents",
                                        fileName,
                                    });
                                },
                            }}
                            autoGroupColumnDef={{
                                headerName: "Name",
                                minWidth: 200,
                                flex: 1,
                                cellRendererParams: {
                                    suppressCount: true,
                                    innerRenderer: FileNameCellRenderer,
                                    innerRendererParams: {
                                        getExtractionUrl,
                                        onViewExtraction,
                                        onViewRentRollExtraction,
                                        getRentRollExtractionUrl,
                                    } as React.ComponentProps<
                                        typeof FileNameCellRenderer
                                    >,
                                },
                                rowGroup: true,
                                cellClass: ["ag-right-aligned-cell"],
                            }}
                            enterNavigatesVertically={true}
                            enterNavigatesVerticallyAfterEdit={true}
                        />
                    </Box>
                    <Box
                        sx={{
                            height: "5rem",
                            borderTop: "1px solid var(--proda-white-130)",
                            padding: "1rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "1rem",
                        }}
                    >
                        <Box>
                            {nonExportableExtractions.length > 0 && (
                                <WarningMessage
                                    sx={{
                                        padding: "10px",
                                    }}
                                >
                                    <Box
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        <Icon icon="triangle-exclamation" />
                                        <Typography variant="body3">
                                            Rent Rolls are unable to be exported
                                            here, please do so from the relevant
                                            Rent Roll.
                                        </Typography>
                                    </Box>
                                </WarningMessage>
                            )}
                        </Box>
                        <Box
                            sx={{
                                padding: "1rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "1rem",
                            }}
                        >
                            <Box
                                sx={{
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {renderSelectionTitle()}
                            </Box>

                            {(selectedDeletableExtractions.length > 0 ||
                                selectedDeletableFiles.length > 0) && (
                                <Button
                                    pattern="danger"
                                    isInline={true}
                                    size={"large"}
                                    onClick={handleDeleteButtonClicked}
                                >
                                    Delete Selected
                                </Button>
                            )}
                            {ExportableExtractions.length > 0 ? (
                                <Button
                                    pattern="secondary"
                                    isInline={true}
                                    size={"large"}
                                    onClick={() => {
                                        handleButtonClick(() =>
                                            onExportExtractions({
                                                extractions:
                                                    selectedExtractions.map(
                                                        e => ({
                                                            extractionId:
                                                                e.extractionId,
                                                            extractionType:
                                                                e.extractionType,
                                                        }),
                                                    ),
                                            }),
                                        );
                                    }}
                                    leadingIcon={"download"}
                                    data-testid={testIds.exportButton}
                                >
                                    Export
                                </Button>
                            ) : (
                                selectedExtractions.length > 0 && (
                                    <Tooltip
                                        content="Rent roll extractions are not exportable. Please select other extractions to export."
                                        placement="top"
                                    >
                                        <Button
                                            pattern="secondary"
                                            isInline={true}
                                            size={"large"}
                                            disabled={true}
                                        >
                                            Export
                                        </Button>
                                    </Tooltip>
                                )
                            )}

                            {(selectedExtractions.length > 0 ||
                                selectedFiles.length > 0) && (
                                <Button
                                    pattern="secondary"
                                    isInline={true}
                                    size={"large"}
                                    onClick={() =>
                                        handleButtonClick(() => {
                                            const fileIds = new Set([
                                                ...selectedExtractions.map(
                                                    e => e.fileId,
                                                ),
                                                ...selectedFiles.map(
                                                    f => f.fileId,
                                                ),
                                            ]);

                                            onDownloadOriginalFiles(
                                                Array.from(fileIds),
                                            );
                                        })
                                    }
                                    leadingIcon={"download"}
                                >
                                    {downloadOriginalsText()}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </>
    );
};

export default DealProcessedFiles;
